import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const SignupForm = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  // Validate invite token with Supabase on component mount
  useEffect(() => {
    const validateInviteToken = async () => {
      const token = searchParams.get('token');
      const type = searchParams.get('type');
      
      // First check: URL structure
      if (!token || type !== 'invite') {
        debugger;
        navigate('/admin/login', { replace: true });
        return;
      }

      try {
        // Second check: Verify token with Supabase
        // This uses Supabase's verifyOtp method to validate the invite token
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'invite'
        });

        if (error || !data) {
          // Invalid or expired token - redirect to login
          console.error('Invalid invite token:', error);
          navigate('/admin/login', { replace: true });
          return;
        }

        // Token is valid - allow signup
        setTokenValid(true);
        
        // Pre-fill email if available from the invite
        if (data.user?.email) {
          setEmail(data.user.email);
        }
      } catch (err) {
        console.error('Error validating invite token:', err);
        navigate('/admin/login', { replace: true });
      } finally {
        setValidatingToken(false);
      }
    };

    validateInviteToken();
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Ensure token is still valid before submission
    if (!tokenValid) {
      setError('Sesión de invitación expirada. Por favor solicita una nueva invitación.');
      return;
    }
    
    // Validación básica
    if (!email || !password || !confirmPassword) {
      setError('Por favor completa todos los campos requeridos');
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor ingresa un email válido');
      return;
    }

    // Validar longitud de contraseña
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    // Validar que las contraseñas coincidan
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      // Crear cuenta con metadata opcional
      const metadata = fullName ? { full_name: fullName } : {};
      await signUp(email, password, metadata);
      
      setSuccess('¡Cuenta creada exitosamente! Revisa tu email para confirmar tu cuenta.');
      
      // Redirigir al login después de 3 segundos
      setTimeout(() => {
        navigate('/admin/login');
      }, 3000);
      
    } catch (err) {
      console.error('Error al crear cuenta:', err);
      
      // Manejar errores específicos de Supabase
      if (err.message.includes('already registered')) {
        setError('Este email ya está registrado. Por favor inicia sesión.');
      } else if (err.message.includes('Invalid email')) {
        setError('Email inválido. Por favor verifica el formato.');
      } else {
        setError('Error al crear la cuenta. Por favor intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while validating token
  if (validatingToken) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Validando invitación...</span>
        </Spinner>
        <p className="mt-3 text-muted">Validando invitación...</p>
      </div>
    );
  }

  // If token validation failed, component will redirect (this shouldn't render)
  if (!tokenValid) {
    return null;
  }

  return (
    <Form onSubmit={handleSubmit}>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success">
          {success}
        </Alert>
      )}

      <Form.Group className="mb-3" controlId="formFullName">
        <Form.Label>Nombre Completo (Opcional)</Form.Label>
        <Form.Control
          type="text"
          placeholder="Juan Pérez"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          disabled={loading}
        />
      </Form.Group>

      <Form.Group className="mb-3" controlId="formEmail">
        <Form.Label>Email *</Form.Label>
        <Form.Control
          type="email"
          placeholder="admin@lumieretica.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          required
        />
      </Form.Group>

      <Form.Group className="mb-3" controlId="formPassword">
        <Form.Label>Contraseña *</Form.Label>
        <Form.Control
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          required
        />
        <Form.Text className="text-muted">
          Mínimo 6 caracteres
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3" controlId="formConfirmPassword">
        <Form.Label>Confirmar Contraseña *</Form.Label>
        <Form.Control
          type="password"
          placeholder="••••••••"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
          required
        />
      </Form.Group>

      <Button
        variant="primary"
        type="submit"
        className="w-100 btn-admin-primary"
        disabled={loading}
      >
        {loading ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            Creando cuenta...
          </>
        ) : (
          'Crear Cuenta'
        )}
      </Button>
    </Form>
  );
};

export default SignupForm;
